// src/utils/invoiceRefund.js — refundscreen-7c3e5a18 (R1d-1), refunddialog-4e9b7c25 (R1d-2)
// HAND-SYNCED MIRROR of rules in functions/utils/invoiceRefund.js:
//   - which payment-history lines are real customer money (the only lines a refund can be taken
//     from) and which are refunds;
//   - how much is left to refund on the invoice and on one payment;
//   - when "reduce the sale" can't be offered because the invoice's tax isn't known;
//   - the three refusals the screen can foresee, in the server's own words.
// The server is the gate (refundInvoicePayment); this copy only decides what the invoice screen
// offers and pre-fills. Every function and message below is identical to the server's;
// invoiceRefundUi.test.js and invoiceRefundDialog.test.js compare them. Change both together.

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : 0));

// Plain-English refusals (shown to the operator as-is) — the ones knowable without QuickBooks.
const MSG = Object.freeze({
  TAX_UNKNOWN_IMPORT: "This invoice was imported without its tax recorded separately, so a credit note can't show the tax reversed. Use \"Return a payment\" instead.",
  TAX_UNKNOWN: "This invoice has no tax amount recorded, so a credit note can't show the tax reversed. Use \"Return a payment\" instead.",
  NO_GST_NUMBER: 'A credit note must show your GST/HST registration number. Add it in Settings → Company Info (Tax ID / GST / HST), then try again.',
  FAILED_BEFORE: 'This refund attempt already failed. Start a new refund.',
});

/** Money the customer actually paid on this invoice (not credits, credit notes or deposits). */
function isCashPaymentRow(e) {
  return !!e && num(e.amount) > 0 && !e.isCredit && !e.isCreditNote && !e.isCreditReversal && !e.depositQuoteId;
}

/** A refund of invoice money (deposit refunds are tracked against the deposit, not here). */
function isInvoiceRefundRow(e) {
  return !!e && e.isRefund === true && num(e.amount) < 0 && !e.depositRefundQuoteId;
}

/** Invoice-wide refundable cash: Σ cash payments − Σ invoice refunds. Deposits reported apart. */
function summarizeRefundable(history) {
  const rows = Array.isArray(history) ? history : [];
  let cashPaid = 0; let refunded = 0; let depositPaid = 0;
  for (const e of rows) {
    if (isCashPaymentRow(e)) cashPaid += num(e.amount);
    else if (isInvoiceRefundRow(e)) refunded += Math.abs(num(e.amount));
    if (e && e.depositQuoteId && num(e.amount) > 0) depositPaid += num(e.amount);
    if (e && e.depositRefundQuoteId && num(e.amount) < 0) depositPaid -= Math.abs(num(e.amount));
  }
  return { cashPaid: r2(cashPaid), refunded: r2(refunded), refundable: r2(cashPaid - refunded), depositPaid: r2(Math.max(0, depositPaid)) };
}

/**
 * What is left to refund on ONE payment, keyed on the id that ties refunds back to it: the Stripe
 * PaymentIntent (card) or the QuickBooks payment id (manual). null = the row has no such id, so
 * only the invoice-wide cap applies.
 */
function sourceRemaining(history, row) {
  const rows = Array.isArray(history) ? history : [];
  if (!row) return null;
  const pi = row.stripePaymentIntentId;
  const qb = row.qbPaymentId && row.qbPaymentId !== 'skipped' ? row.qbPaymentId : null;
  if (pi) {
    const paid = rows.filter((e) => isCashPaymentRow(e) && e.stripePaymentIntentId === pi).reduce((s, e) => s + num(e.amount), 0);
    const back = rows.filter((e) => e && e.refundedPaymentIntentId === pi).reduce((s, e) => s + Math.abs(num(e.amount)), 0);
    return { key: 'stripePaymentIntentId', id: pi, paid: r2(paid), remaining: r2(paid - back) };
  }
  if (qb) {
    const paid = rows.filter((e) => isCashPaymentRow(e) && e.qbPaymentId === qb).reduce((s, e) => s + num(e.amount), 0);
    const back = rows.filter((e) => e && e.refundOfQbPaymentId === qb).reduce((s, e) => s + Math.abs(num(e.amount)), 0);
    return { key: 'qbPaymentId', id: qb, paid: r2(paid), remaining: r2(paid - back) };
  }
  return null;
}

/**
 * Why tax on this invoice can't be known, or null. Decision 5 (Marcus 2026-10-07), census
 * 2026-10-07: imported invoices whose importer folded tax into the total (GC Septic's
 * QuickBooks migration `qbinv_*`, Jobber imports showing $0 tax), and any invoice with no
 * numeric taxAmount. Every in-app invoice has a real taxAmount and is NOT blocked.
 */
function taxUnknownReason(inv, invoiceId) {
  const t = inv ? inv.taxAmount : undefined;
  if (typeof t !== 'number' || !Number.isFinite(t)) return 'noTaxAmount';
  const imported = !!(inv.importId || inv.importedAt || String(invoiceId || '').startsWith('qbinv_'));
  if (imported && !(t > 0)) return 'importedNoTax';
  return null;
}

export { MSG, r2, isCashPaymentRow, isInvoiceRefundRow, summarizeRefundable, sourceRemaining, taxUnknownReason };
